import React, { useState } from "react";
import { Plus, Trash2, Check } from "lucide-react";

// ============================================================
// Recuadros editables: "12×40", "60 s", "3 tandas"…
//
// Las técnicas (piramidal, EMOM, rest-pause, drop set) se leen y se
// cambian en la misma planilla: cada variable es un recuadro, se toca
// y debajo aparecen sus campos. Lo usan el planificador del coach y
// la vista de lista del alumno.
// ============================================================

// Lo que hizo contra lo que se pidió: verde igual, rojo se pasó,
// amarillo le sobró. Los mismos colores de la tarjeta.
export const COLOR_ESTADO = {
  match:  { color: "#34d399", background: "#34d3991f" },
  harder: { color: "#f87171", background: "#f871711f" },
  easier: { color: "#facc15", background: "#facc151f" },
};
const NEUTRO = { color: "#a3a3a3", background: "#ffffff0d" };

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isNaN(n) ? null : n;
};

// Una serie de piramidal: primero manda el peso; con el mismo peso,
// las repeticiones.
export function estadoSerie(hizo, pedido) {
  if (!hizo || !pedido) return null;
  const hp = num(hizo.peso), pp = num(pedido.peso);
  const hr = num(hizo.reps), pr = num(pedido.reps);
  if (hp == null && hr == null) return null;
  if (hp != null && pp != null && hp !== pp) return hp > pp ? "harder" : "easier";
  if (hr != null && pr != null && hr !== pr) return hr > pr ? "harder" : "easier";
  return "match";
}

// chips: [{ etiqueta, estado, titulo, nota, campos: [{ clave, label, valor }],
//           boton, quitable, sugerencia: { texto, valores } }]
// onGuardar(indice, valores) · onQuitar(indice) · onAgregar()
// En una planilla que se desliza de costado, el editor no puede ir
// dentro de la celda: quedaría fuera de la pantalla. Con `elegido` y
// `onElegir`, quien lo usa dibuja <EditorChip> en una fila aparte.
export function Chips({ chips, onGuardar, onQuitar, onAgregar, ancho = 290, elegido, onElegir, envolver = false }) {
  const controlado = typeof onElegir === "function";
  const [propio, setPropio] = useState(null);
  const abierto = controlado ? elegido : propio;

  const abrir = (i) => {
    const nuevo = abierto === i ? null : i;
    if (controlado) onElegir(nuevo); else setPropio(nuevo);
  };
  const ch = !controlado && abierto != null ? chips[abierto] : null;

  return (
    <div className="inline-block align-middle">
      <div className={`flex items-center gap-1 ${envolver ? "flex-wrap" : "flex-nowrap"}`}>
        {chips.map((c, i) => {
          const tono = COLOR_ESTADO[c.estado] || NEUTRO;
          return (
            <button key={i} type="button" onClick={() => (c.campos?.length ? abrir(i) : c.onTocar?.())}
              className="rounded-md px-1.5 py-1 text-[11.5px] font-semibold whitespace-nowrap"
              style={{ ...tono,
                       border: `1px solid ${abierto === i ? "#38bdf8" : COLOR_ESTADO[c.estado] ? "transparent" : "#525252"}` }}>
              {c.etiqueta}
            </button>
          );
        })}
        {onAgregar && (
          <button type="button" onClick={onAgregar} title="Sumar una serie"
            className="rounded-md px-1.5 py-1 border border-dashed border-neutral-600 text-neutral-400 hover:text-sky-400">
            <Plus size={12} />
          </button>
        )}
      </div>

      {ch && (
        <EditorChip key={abierto} chip={ch} ancho={ancho}
          onGuardar={(v) => onGuardar(abierto, v)}
          onQuitar={onQuitar ? () => onQuitar(abierto) : null}
          onCerrar={() => setPropio(null)} />
      )}
    </div>
  );
}

// Los campos de un recuadro, con sus botones.
export function EditorChip({ chip, onGuardar, onQuitar, onCerrar, ancho = 290 }) {
  const [vals, setVals] = useState(() =>
    Object.fromEntries((chip.campos || []).map((c) => [c.clave, c.valor ?? ""])));
  return (
    <div className="mt-1.5 rounded-lg border p-2.5 space-y-2 bg-neutral-800 whitespace-normal text-left"
      style={{ borderColor: "#38bdf866", width: ancho, maxWidth: "100%" }}>
      {chip.titulo && <p className="text-[10px] uppercase tracking-wider text-neutral-500">{chip.titulo}</p>}
      {chip.nota && (
        <p className="text-[11px] text-neutral-400 flex items-center gap-2 flex-wrap">
          {chip.nota}
          {chip.sugerencia && (
            <button type="button" onClick={() => setVals((v) => ({ ...v, ...chip.sugerencia.valores }))}
              className="text-sky-400 border border-sky-400/30 rounded px-1.5 py-0.5 font-medium">
              {chip.sugerencia.texto}
            </button>
          )}
        </p>
      )}
      <div className="flex items-end gap-2">
        {chip.campos.map((c) => (
          <label key={c.clave} className="flex-1 min-w-0 text-[10px] text-neutral-500">
            {c.label}
            <input inputMode="decimal" value={vals[c.clave] ?? ""}
              onChange={(e) => setVals((v) => ({ ...v, [c.clave]: e.target.value }))}
              className="mt-0.5 w-full bg-neutral-700 border border-neutral-600 focus:border-sky-400 focus:outline-none rounded-md px-2 py-1.5 text-base font-semibold text-center text-neutral-100" />
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2">
        {onQuitar && chip.quitable && (
          <button type="button" onClick={() => { onQuitar(); onCerrar(); }}
            className="text-[11px] text-neutral-500 hover:text-red-400 inline-flex items-center gap-1">
            <Trash2 size={11} /> Quitar serie
          </button>
        )}
        <button type="button" onClick={onCerrar}
          className="ml-auto text-[11px] text-neutral-500 px-2 py-1.5">
          Cancelar
        </button>
        <button type="button" onClick={() => { onGuardar(vals); onCerrar(); }}
          className="rounded-md bg-sky-400 text-black text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
          <Check size={13} /> {chip.boton || "Listo"}
        </button>
      </div>
    </div>
  );
}

// Las variables de cada técnica como recuadros para el coach.
// Devuelve null si la técnica no tiene variables de este tipo.
export function chipsDeTecnica(tecnica, cfg = {}) {
  const c = cfg || {};
  const uno = (clave, valor, etiqueta, label) => ({
    etiqueta, campos: [{ clave, label, valor }],
  });
  if (tecnica === "emom") return [
    uno("segundos", c.segundos ?? 60, `${c.segundos ?? 60} s`, "Segundos por bloque"),
    uno("repsInicio", c.repsInicio ?? 1, `arranca con ${c.repsInicio ?? 1}`, "Reps del primer bloque"),
    uno("incremento", c.incremento ?? 1, `+${c.incremento ?? 1} por bloque`, "Suma por bloque"),
  ];
  if (tecnica === "restpause") return [
    uno("tandas", c.tandas ?? 3, `${c.tandas ?? 3} tandas`, "Tandas"),
    uno("pausa", c.pausa ?? 15, `${c.pausa ?? 15} s pausa`, "Segundos de pausa"),
  ];
  if (tecnica === "dropset") return [
    uno("caidas", c.caidas ?? 3, `${c.caidas ?? 3} caídas`, "Caídas de peso"),
  ];
  return null;
}

// Guardar un recuadro de técnica: el valor como número.
export const conValor = (cfg, valores) => {
  const next = { ...(cfg || {}) };
  for (const [k, v] of Object.entries(valores)) {
    const n = num(v);
    if (n != null) next[k] = n;
  }
  return next;
};

// ============================================================
// Títulos de columnas fijos arriba de una planilla que se desliza.
//
// La planilla se desliza de costado dentro de su recuadro; los títulos
// van aparte, pegados arriba al bajar la página. Para que acompañen el
// deslizamiento sin trabarse, no se "desplazan": se corren con un
// transform, que el teléfono mueve con la placa de video y no recalcula
// la página. El primer título (el del ejercicio) queda quieto, igual
// que la primera columna.
// ============================================================
export function CabeceraFija({ id, cols, className = "" }) {
  const [primera, ...resto] = cols;
  return (
    <div
      ref={(el) => {
        if (!el) return;
        const cab = document.querySelector("[data-cabecera]");
        el.style.top = `${cab ? cab.offsetHeight : 60}px`;
      }}
      className={`sticky z-20 flex bg-neutral-800 rounded-t-xl border-b border-neutral-700 overflow-hidden text-xs text-neutral-500 font-medium ${className}`}>
      <div className="shrink-0 px-2 py-2 whitespace-nowrap bg-neutral-800 relative z-10" style={{ width: primera[1] }}>
        {primera[0]}
      </div>
      <div className="flex-1 overflow-hidden">
        <div id={id} className="flex" style={{ willChange: "transform" }}>
          {resto.map(([t, w], k) => (
            <div key={k} className="shrink-0 px-2 py-2 whitespace-nowrap" style={{ width: w }}>{t}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Lo que hace el recuadro que se desliza: corre los títulos con él.
export const seguirCabecera = (id) => (e) => {
  const t = document.getElementById(id);
  if (t) t.style.transform = `translate3d(${-e.currentTarget.scrollLeft}px,0,0)`;
};

// Para el recuadro que se desliza: que el gesto no arrastre la página
// ni dispare el "volver" del navegador al llegar al borde.
export const ESTILO_DESLIZA = {
  overscrollBehaviorX: "contain",
  WebkitOverflowScrolling: "touch",
  touchAction: "pan-x pan-y",
};
